import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { makeStyles } from "@material-ui/core/styles";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import moment from "moment-timezone";
import * as XLSX from "xlsx";
import {
  Autocomplete,
  Badge,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputBase,
  LinearProgress,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import FilterListIcon from "@mui/icons-material/FilterList";
import ViewColumnIcon from "@mui/icons-material/ViewColumn";
import RefreshIcon from "@mui/icons-material/Refresh";
import SearchOffIcon from "@mui/icons-material/SearchOff";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddCommentIcon from "@mui/icons-material/AddComment";
import SummarizeIcon from "@mui/icons-material/Summarize";

import API_BASE_URL from "../../config/api";
import { COLORS } from "../UserDashboard/dashboardConfig";
import { usePageStyles } from "../common/pageStyles";
import { useSession } from "../common/useSession";
import { useViewPreferences } from "../common/useViewPreferences";
import FilterDrawer, { FilterCard, countFilled } from "../common/FilterDrawer";
import ColumnsMenu from "../common/ColumnsMenu";

const BASE_URL = API_BASE_URL;
const PAGE_SIZE_OPTIONS = [20, 50, 100, 200];
const FILTER_DEBOUNCE_MS = 250;
const BULK_LIMIT = 200;
const YES_NO_VALUES = ["Tak", "Nie"];

const PILL_TONES = {
  green: { bg: "#e8f5e9", fg: "#1b5e20" },
  amber: { bg: "#fff8e1", fg: "#b26a00" },
  red: { bg: "#ffebee", fg: "#c62828" },
  blue: { bg: "#e3f2fd", fg: "#1565c0" },
  gray: { bg: "#eeeeee", fg: "#616161" },
};

const STATUS_TONES = {
  "Do przygotowania": "blue",
  Zrealizowane: "green",
  Anulowane: "gray",
};

const isYes = (value) => /^tak$/i.test(String(value ?? "").trim());

const authHeaders = () => ({
  "Content-Type": "application/json",
  Authorization: `Bearer ${localStorage.getItem("token")}`,
});

const createHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem("token") || ""}`,
  "X-Use-New-Db": localStorage.getItem("useNewDb") || "false",
});

const useDebounced = (value, delay) => {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);
  return debounced;
};

const parseMultiSearchValues = (value) =>
  (value || "")
    .split(/[\n,]+/)
    .map((item) => item.trim().toLowerCase())
    .filter(Boolean);

const sortPl = (values) =>
  [...new Set(values.filter((v) => v !== null && v !== undefined && String(v).trim()))].sort(
    (a, b) => String(a).localeCompare(String(b), "pl", { sensitivity: "base" }),
  );

const toMoment = (value, localTime) =>
  localTime ? moment(value, "YYYY-MM-DDTHH:mm:ss.SSSZ") : moment.utc(value, "YYYY-MM-DDTHH:mm:ss.SSSZ");

const formatDay = (value, localTime) => {
  const parsed = toMoment(value, localTime);
  return parsed.isValid() ? parsed.format("DD-MM-YYYY") : "Brak danych";
};

const formatDateTime = (value, localTime) => {
  const text = String(value ?? "");
  if (!text || text.startsWith("0000-00-00")) return "Brak danych";
  const parsed = toMoment(value, localTime);
  return parsed.isValid() ? parsed.format("DD-MM-YYYY, HH:mm") : "Brak danych";
};

// Czas od utworzenia zgloszenia; dla zakonczonych zawsze "Zakończono".
const elapsedSince = (createdAt, status) => {
  if (/^zrealizowane$/i.test(status || "") || status === "Anulowane") return "Zakończono";
  const duration = moment.duration(moment().diff(moment(createdAt)));
  const months = duration.months();
  const days = duration.days();
  const hours = duration.hours();
  const minutes = duration.minutes();
  if (months > 0) return `${months}m ${days}d ${hours}h ${minutes}m`;
  if (days >= 1) return `${days}d ${hours}h ${minutes}m`;
  return `${hours}h ${minutes}m`;
};

const emptyFilters = (config) => {
  const empty = { search: "", nrRef: "", date: "" };
  config.filters.forEach((f) => {
    empty[f.key] = f.type === "multi" ? [] : "";
  });
  config.ranges.forEach((r) => {
    empty[`${r.key}From`] = "";
    empty[`${r.key}To`] = "";
  });
  return empty;
};

const readSavedFilters = (config, storageKey) => {
  const base = emptyFilters(config);
  try {
    const saved = JSON.parse(localStorage.getItem(storageKey));
    if (!saved || typeof saved !== "object") return base;
    Object.keys(base).forEach((key) => {
      if (Array.isArray(base[key])) {
        if (Array.isArray(saved[key])) base[key] = saved[key];
      } else if (typeof saved[key] === "string") {
        base[key] = saved[key];
      }
    });
  } catch (error) {
    console.error("Nie udało się wczytać filtrów:", error);
  }
  return base;
};

const describeFilters = (config, f) => {
  const chips = [];
  const push = (id, label, clear) => chips.push({ id, label, clear });
  if (f.search) push("search", `Szukaj: ${f.search}`, { search: "" });
  if (f.nrRef) push("nrRef", `Nr ref: ${f.nrRef}`, { nrRef: "" });
  if (f.date) push("date", `Data: ${f.date}`, { date: "" });
  config.filters.forEach((def) => {
    const value = f[def.key];
    if (def.type === "multi") {
      if (value.length > 0) push(def.key, `${def.label}: ${value.join(", ")}`, { [def.key]: [] });
    } else if (value) {
      push(def.key, `${def.label}: ${value}`, { [def.key]: "" });
    }
  });
  config.ranges.forEach((r) => {
    const from = f[`${r.key}From`];
    const to = f[`${r.key}To`];
    if (from || to) {
      push(r.key, `${r.label}: ${from || "…"} – ${to || "…"}`, {
        [`${r.key}From`]: "",
        [`${r.key}To`]: "",
      });
    }
  });
  return chips;
};

const applyFilters = (config, rows, f) => {
  let result = rows;

  config.filters.forEach((def) => {
    const value = f[def.key];
    if (def.type === "multi" && value.length > 0) {
      result = result.filter((row) => value.includes(row[def.key]));
    } else if (def.type === "yesno" && value) {
      const test = def.test || ((row) => isYes(row[def.key]));
      result = result.filter((row) => (value === "Tak" ? test(row) : !test(row)));
    } else if (def.type === "text" && value) {
      const needle = value.toLowerCase();
      result = result.filter((row) => String(row[def.key] ?? "").toLowerCase().includes(needle));
    }
  });

  const tokens = parseMultiSearchValues(f.nrRef);
  if (tokens.length > 0) {
    result = result.filter((row) => {
      const value = String(row.nrRef || "").toLowerCase();
      return tokens.some((token) => value.includes(token));
    });
  }

  if (f.date) {
    result = result.filter((row) =>
      toMoment(row.utworzono, config.localTime).format("DD-MM-YYYY").includes(f.date),
    );
  }

  config.ranges.forEach((r) => {
    const from = f[`${r.key}From`];
    const to = f[`${r.key}To`];
    if (!from && !to) return;
    const start = from ? new Date(`${from}T00:00:00`) : null;
    const end = to ? new Date(`${to}T23:59:59.999`) : null;
    result = result.filter((row) => {
      const value = new Date(row[r.field]);
      if (Number.isNaN(value.getTime())) return false;
      return (!start || value >= start) && (!end || value <= end);
    });
  });

  const query = f.search.trim().toLowerCase();
  if (query) {
    result = result.filter((row) =>
      Object.values(row)
        .map((value) => (value == null ? "" : value.toString()))
        .join(" ")
        .toLowerCase()
        .includes(query),
    );
  }
  return result;
};

const useRowStyles = makeStyles(() => ({
  rowDanger: {
    "& > td": { backgroundColor: "#fff1f1" },
    "&:hover > td": { backgroundColor: "#ffe3e3" },
    "& > td:first-child": { boxShadow: `inset 4px 0 0 ${COLORS.danger}` },
  },
}));

const Pill = ({ tone, children }) => {
  const classes = usePageStyles();
  const colors = PILL_TONES[tone] || PILL_TONES.gray;
  return (
    <span className={classes.pill} style={{ background: colors.bg, color: colors.fg }}>
      {children}
    </span>
  );
};

// -----------------------------------------------------------WIERSZ-------------
const Row = React.memo(
  ({ row, config, columns, isSelected, onToggleSelect, onOpen, onDelete, canDelete }) => {
    const classes = usePageStyles();
    const rowClasses = useRowStyles();
    const [open, setOpen] = useState(false);
    const [loadingDetails, setLoadingDetails] = useState(false);
    const [editHistory, setEditHistory] = useState([]);
    const [comments, setComments] = useState([]);

    const handleExpandClick = async () => {
      if (!open) {
        setLoadingDetails(true);
        const fetchList = (url, label) =>
          axios
            .get(url, { headers: authHeaders() })
            .then((r) => r.data || [])
            .catch((error) => {
              console.error(`Error fetching ${label}:`, error);
              return [];
            });
        const [history, rowComments] = await Promise.all([
          fetchList(`${BASE_URL}/history/${row.id}`, "edit history"),
          fetchList(`${BASE_URL}/comments/${row.id}`, "comments"),
        ]);
        setEditHistory(history);
        setComments(rowComments);
        setLoadingDetails(false);
      }
      setOpen((prev) => !prev);
    };

    const empty = <span className={classes.muted}>—</span>;
    const text = (value) =>
      value || value === 0 ? (
        <Tooltip title={String(value)} enterDelay={600}>
          <span className={classes.ellipsis}>{value}</span>
        </Tooltip>
      ) : (
        empty
      );

    const renderCell = (column) => {
      const value = row[column.id];
      switch (column.kind) {
        case "status":
          return value ? <Pill tone={STATUS_TONES[value] || "gray"}>{value}</Pill> : empty;
        case "yes":
          return isYes(value) ? <Pill tone="amber">{value}</Pill> : empty;
        case "date":
          return formatDay(value, config.localTime);
        case "dateTime":
          return formatDateTime(value, config.localTime);
        case "elapsed":
          return elapsedSince(row.utworzono, row.statusRealizacji);
        case "duplicate":
          return row.duplikat === 1 ? (
            <Tooltip title="Duplikat: numer przesyłki już istnieje w bazie">
              <span className={classes.inline}>
                {value}
                <Pill tone="red">Duplikat</Pill>
              </span>
            </Tooltip>
          ) : (
            text(value)
          );
        default:
          return text(value);
      }
    };

    const tint = config.tint(row);
    const tintClass = tint === "warn" ? classes.rowWarn : tint === "danger" ? rowClasses.rowDanger : "";

    return (
      <>
        <TableRow
          hover={false}
          className={`${classes.row} ${tintClass} ${isSelected ? classes.rowSelected : ""}`}
        >
          <TableCell
            padding="checkbox"
            className={classes.stickyCell}
            style={{ left: 0, width: 52, minWidth: 52 }}
          >
            <Checkbox
              size="small"
              color="primary"
              checked={isSelected}
              onChange={() => onToggleSelect(row)}
              inputProps={{ "aria-label": `Zaznacz zgłoszenie ${row.nrRef ?? row.id}` }}
            />
          </TableCell>
          <TableCell
            padding="none"
            className={classes.stickyCell}
            style={{ left: 52, width: 40, minWidth: 40 }}
          >
            <Tooltip title="Historia zmian i komentarze">
              <IconButton size="small" onClick={handleExpandClick} aria-label="Pokaż historię">
                {loadingDetails ? (
                  <CircularProgress size={18} />
                ) : open ? (
                  <KeyboardArrowUpIcon fontSize="small" />
                ) : (
                  <KeyboardArrowDownIcon fontSize="small" />
                )}
              </IconButton>
            </Tooltip>
          </TableCell>
          <TableCell
            className={`${classes.stickyCell} ${classes.stickyEdge}`}
            style={{ left: 92, minWidth: 150, whiteSpace: "nowrap" }}
          >
            <button type="button" className={classes.link} onClick={(e) => onOpen(row, e)}>
              {row.nrRef || "—"}
            </button>
          </TableCell>
          {columns.map((column) => (
            <TableCell key={column.id} style={{ minWidth: column.width, maxWidth: column.width }}>
              {renderCell(column)}
            </TableCell>
          ))}
          <TableCell style={{ width: 96, minWidth: 96 }}>
            <div className={classes.actions}>
              <Tooltip title="Szczegóły / edycja">
                <IconButton
                  size="small"
                  onClick={(e) => onOpen(row, e)}
                  onAuxClick={(e) => onOpen(row, e)}
                  aria-label="Szczegóły / edycja"
                >
                  <OpenInNewIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              {canDelete && (
                <Tooltip title="Usuń zgłoszenie">
                  <IconButton size="small" onClick={() => onDelete(row)} aria-label="Usuń zgłoszenie">
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </div>
          </TableCell>
        </TableRow>
        <TableRow>
          <TableCell
            colSpan={columns.length + 4}
            className={`${classes.detailCell} ${open ? classes.detailCellOpen : ""}`}
          >
            <Collapse in={open} timeout="auto" unmountOnExit>
              <div className={classes.detailWrap}>
                <div className={classes.detailCard}>
                  <div className={classes.detailTitle}>Historia zmian</div>
                  <div className={classes.detailList}>
                    {editHistory.length === 0 ? (
                      <div className={classes.detailEmpty}>Brak historii zmian dla tego zgłoszenia.</div>
                    ) : (
                      editHistory.map((change) => (
                        <div key={change.id} className={classes.detailItem}>
                          <strong>{change.fieldName || "-"}</strong>
                          <div className={classes.detailChange}>
                            <span style={{ color: COLORS.danger }}>{change.oldValue || "—"}</span>
                            {" → "}
                            <span style={{ color: COLORS.success, fontWeight: 600 }}>
                              {change.newValue || "—"}
                            </span>
                          </div>
                          <div className={classes.detailMeta}>
                            {change.editedBy || "-"} ·{" "}
                            {formatDateTime(change.editedAt, config.localTime)}
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
                <div className={classes.detailCard}>
                  <div className={classes.detailTitle}>Komentarze</div>
                  <div className={classes.detailList}>
                    {comments.length === 0 ? (
                      <div className={classes.detailEmpty}>Brak komentarzy dla tego zgłoszenia.</div>
                    ) : (
                      comments.map((comment) => (
                        <div key={comment.id} className={classes.detailItem}>
                          <div>{comment.comment || "-"}</div>
                          <div className={classes.detailMeta}>
                            {comment.username || "-"} ·{" "}
                            {moment(comment.created_at).format("DD.MM.YYYY, HH:mm")}
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </div>
            </Collapse>
          </TableCell>
        </TableRow>
      </>
    );
  },
);

// -----------------------------------------------------------WIDOK--------------
/**
 * Lista Magazynu Warszawa w nowym wygladzie. Co pokazuje i skad bierze dane okresla `config`
 * (patrz magazynListConfigs.js). Wszystkie dane pobierane sa naraz, filtrowanie dziala w przegladarce.
 */
const MagazynListPage = ({ config }) => {
  const classes = usePageStyles();
  const navigate = useNavigate();
  const { username, role } = useSession();
  const canDelete = config.deleteRoles.includes(role) || config.deleteUsernames.includes(username);
  const filtersStorageKey = `mwFilters.${config.id}`;

  // Widoczne kolumny i rozmiar strony - w bazie (per uzytkownik), localStorage tylko jako cache.
  const prefs = useViewPreferences({
    view: config.view,
    storageKey: `columnsView.${config.view}`,
    defaultHiddenColumns: [],
    pageSizeOptions: PAGE_SIZE_OPTIONS,
    defaultPageSize: 50,
  });
  const rowsPerPage = prefs.pageSize;

  const [apiData, setApiData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [filters, setFilters] = useState(() => readSavedFilters(config, filtersStorageKey));
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [columnsAnchor, setColumnsAnchor] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkText, setBulkText] = useState("");
  const [bulkSaving, setBulkSaving] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [reportStart, setReportStart] = useState("");
  const [reportEnd, setReportEnd] = useState("");
  const [reportDirections, setReportDirections] = useState([]);
  const searchRef = useRef(null);

  const debouncedFilters = useDebounced(filters, FILTER_DEBOUNCE_MS);

  const columns = useMemo(
    () => config.columns.filter((c) => !prefs.hiddenColumns.includes(c.id)),
    [config, prefs.hiddenColumns],
  );

  useEffect(() => {
    const timeout = setTimeout(() => {
      try {
        localStorage.setItem(filtersStorageKey, JSON.stringify(filters));
      } catch (error) {
        console.error("Nie udało się zapisać filtrów:", error);
      }
    }, 200);
    return () => clearTimeout(timeout);
  }, [filters, filtersStorageKey]);

  // Skrot "/" ustawia fokus w wyszukiwarce.
  useEffect(() => {
    const onKey = (event) => {
      const tag = (event.target.tagName || "").toLowerCase();
      if (event.key === "/" && !["input", "textarea", "select"].includes(tag)) {
        event.preventDefault();
        searchRef.current?.focus();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const handleLogout = useCallback(() => {
    localStorage.clear();
    delete axios.defaults.headers.common["Authorization"];
    delete axios.defaults.headers.common["X-Use-New-Db"];
    toast.success("Wylogowano.", { autoClose: 500, onClose: () => navigate("/login") });
  }, [navigate]);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(`${BASE_URL}${config.endpoint}`, { headers: createHeaders() });
      setApiData(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("❌ Błąd pobierania danych:", error);
      if (error.response?.status === 401) handleLogout();
      else toast.error("Nie udało się pobrać danych.");
    } finally {
      setLoading(false);
    }
  }, [config, handleLogout]);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const token = localStorage.getItem("token");
        if (!token) {
          handleLogout();
          return;
        }
        const { data: userData } = await axios.get(`${BASE_URL}/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!mounted) return;
        localStorage.setItem("useNewDb", userData.useNewDb ? "true" : "false");
        await fetchData();
      } catch (error) {
        console.error("❌ Błąd inicjalizacji:", error);
        if (mounted) handleLogout();
      }
    })();
    return () => {
      mounted = false;
    };
  }, [fetchData, handleLogout]);

  const filteredData = useMemo(
    () => applyFilters(config, apiData, debouncedFilters),
    [config, apiData, debouncedFilters],
  );

  useEffect(() => {
    setPage(0);
  }, [filteredData]);

  // Zaznaczenie obejmuje tylko wiersze, ktore nadal sa widoczne po filtrowaniu.
  useEffect(() => {
    setSelectedIds((prev) => {
      if (prev.length === 0) return prev;
      const visible = new Set(filteredData.map((row) => row.id));
      const next = prev.filter((id) => visible.has(id));
      return next.length === prev.length ? prev : next;
    });
  }, [filteredData]);

  const pageRows = useMemo(
    () => filteredData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [filteredData, page, rowsPerPage],
  );

  const optionsByKey = useMemo(() => {
    const options = {};
    config.filters.forEach((def) => {
      if (def.type === "multi") {
        options[def.key] = def.options || sortPl(apiData.map((row) => row[def.key]));
      }
    });
    return options;
  }, [config, apiData]);

  const updateFilters = useCallback((patch) => setFilters((prev) => ({ ...prev, ...patch })), []);
  const clearAllFilters = () => setFilters(emptyFilters(config));

  const activeChips = useMemo(() => describeFilters(config, filters), [config, filters]);
  const activeCount = useMemo(
    () => describeFilters(config, { ...filters, search: "" }).length,
    [config, filters],
  );
  const hasFilters = activeChips.length > 0;

  // --- zaznaczanie ---
  const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds]);
  const selectedRows = useMemo(
    () => apiData.filter((row) => selectedSet.has(row.id)),
    [apiData, selectedSet],
  );

  const toggleRowSelected = useCallback((row) => {
    setSelectedIds((prev) => (prev.includes(row.id) ? prev.filter((id) => id !== row.id) : [...prev, row.id]));
  }, []);

  const pageAllSelected = pageRows.length > 0 && pageRows.every((row) => selectedSet.has(row.id));
  const pageSomeSelected = !pageAllSelected && pageRows.some((row) => selectedSet.has(row.id));

  const toggleSelectAllOnPage = () => {
    const ids = pageRows.map((row) => row.id);
    setSelectedIds((prev) =>
      pageAllSelected ? prev.filter((id) => !ids.includes(id)) : [...new Set([...prev, ...ids])],
    );
  };

  const handleOpenDetail = useCallback(
    (row, event) => {
      const url = `/detail/${encodeURIComponent(row.nrRef)}/${encodeURIComponent(row.id)}`;
      if (event?.button === 1 || event?.ctrlKey || event?.metaKey) {
        window.open(url, "_blank");
      } else {
        navigate(url, { state: { from: config.detailFrom } });
      }
    },
    [navigate, config],
  );

  const handleConfirmDelete = async () => {
    const row = deleteTarget;
    if (!row || !canDelete) return;
    try {
      await axios.delete(`${BASE_URL}/delete/${row.id}`, { headers: authHeaders() });
      toast.success("Poprawnie usunięto zgłoszenie!", { autoClose: 3000 });
      setDeleteTarget(null);
      fetchData();
    } catch (error) {
      console.error("Error deleting row:", error);
      toast.error("Wystąpił błąd podczas usuwania.", { autoClose: 3000 });
    }
  };

  // --- komentarz zbiorczy ---
  const handleSaveBulkComment = async () => {
    const comment = bulkText.trim();
    if (!comment) {
      toast.warning("Wpisz treść komentarza.");
      return;
    }
    if (selectedRows.length === 0) {
      toast.warning("Brak zaznaczonych rekordów do zapisu komentarza.");
      return;
    }
    if (selectedRows.length > BULK_LIMIT) {
      toast.warning(`Maksymalnie można zapisać komentarz dla ${BULK_LIMIT} rekordów na raz.`);
      return;
    }
    try {
      setBulkSaving(true);
      const items = selectedRows.map((row) => ({
        id: row.id,
        nrRef: row.nrRef,
        rodzajTransportu: row.rodzajTransportu,
        statusRealizacji: row.statusRealizacji,
        rodzaj: row.rodzaj,
        recipients: [row.createdBy]
          .filter((item) => typeof item === "string")
          .map((item) => item.trim())
          .filter((item) => item && item !== username),
      }));
      const response = await axios.post(
        `${BASE_URL}/comments/bulk/addWithNotification`,
        {
          items,
          comment,
          username,
          timestamp: moment().tz("Europe/Warsaw").format("YYYY-MM-DD HH:mm:ss"),
        },
        { headers: { ...createHeaders(), "Content-Type": "application/json" } },
      );
      toast.success(`Komentarz zapisano dla ${response?.data?.processed ?? items.length} ${config.bulkNoun}.`);
      setBulkText("");
      setSelectedIds([]);
      setBulkOpen(false);
      await fetchData();
    } catch (error) {
      toast.error(error?.response?.data?.message || "Wystąpił błąd podczas zapisu komentarzy.");
    } finally {
      setBulkSaving(false);
    }
  };

  // --- raport XLSX ---
  const closeReport = () => {
    setReportOpen(false);
    setReportDirections([]);
  };

  const handleGenerateReport = async () => {
    const report = config.report;
    if (!reportStart || !reportEnd) {
      toast.error("Proszę wybrać obie daty");
      return;
    }
    const loadingToast = toast.info("Generowanie raportu...", { autoClose: false });
    try {
      let allComments = [];
      try {
        const response = await axios.get(`${BASE_URL}/comments`, { headers: createHeaders() });
        allComments = response.data || [];
      } catch (error) {
        console.error("Error fetching all comments:", error);
      }

      const start = new Date(`${reportStart}T00:00:00`);
      const end = new Date(`${reportEnd}T23:59:59`);
      let rows = apiData.filter((item) => {
        const itemDate = new Date(item[report.dateField]);
        return itemDate >= start && itemDate <= end;
      });
      if (reportDirections.length > 0) {
        rows = rows.filter((item) => reportDirections.includes(item.kierunek));
      }

      const reportData = rows.map((item) =>
        report.row(
          item,
          allComments.filter((comment) => comment.customId === item.id),
        ),
      );

      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(reportData), report.sheet);
      const suffix = reportDirections.length > 0 ? `_${reportDirections.join("_")}` : "";
      XLSX.writeFile(wb, report.fileName(reportStart, reportEnd, suffix));

      toast.dismiss(loadingToast);
      toast.success("Raport został wygenerowany pomyślnie!");
      closeReport();
    } catch (error) {
      console.error("Error generating report:", error);
      toast.dismiss(loadingToast);
      toast.error("Błąd podczas generowania raportu");
    }
  };

  // --- pola panelu filtrow ---
  const renderFilter = (def) => {
    if (def.type === "multi") {
      return (
        <Autocomplete
          key={def.key}
          multiple
          size="small"
          options={optionsByKey[def.key] || []}
          value={filters[def.key]}
          onChange={(_, value) => updateFilters({ [def.key]: value })}
          noOptionsText="Brak opcji"
          renderInput={(params) => <TextField {...params} label={def.label} />}
        />
      );
    }
    if (def.type === "yesno") {
      return (
        <TextField
          key={def.key}
          select
          label={def.label}
          size="small"
          fullWidth
          value={filters[def.key]}
          onChange={(e) => updateFilters({ [def.key]: e.target.value })}
        >
          <MenuItem value="" style={{ fontWeight: "bold" }}>
            Wszystko
          </MenuItem>
          {YES_NO_VALUES.map((item) => (
            <MenuItem key={item} value={item}>
              {item}
            </MenuItem>
          ))}
        </TextField>
      );
    }
    return (
      <TextField
        key={def.key}
        label={def.label}
        size="small"
        fullWidth
        autoComplete="off"
        value={filters[def.key]}
        onChange={(e) => updateFilters({ [def.key]: e.target.value })}
      />
    );
  };

  const filterCount = (defs) => countFilled(...defs.map((def) => filters[def.key]));
  const rangeCount = countFilled(
    ...config.ranges.map((r) => filters[`${r.key}From`] || filters[`${r.key}To`]),
  );

  const headerSticky = [
    { left: 0, width: 52 },
    { left: 52, width: 40 },
    { left: 92, width: 150 },
  ];
  const stickyHead = (index, extra = {}) => ({
    left: headerSticky[index].left,
    width: headerSticky[index].width,
    minWidth: headerSticky[index].width,
    ...extra,
  });

  return (
    <>
      <div className={classes.pageHeader}>
        <div>
          <h1 className={classes.title}>{config.title}</h1>
          <div className={classes.subtitle}>
            <span>
              Magazyn Warszawa · <strong style={{ color: "#212121" }}>{filteredData.length}</strong>{" "}
              {hasFilters ? `${config.noun} spełnia filtry` : config.noun}
            </span>
            <Tooltip title="Odśwież teraz">
              <IconButton size="small" onClick={fetchData} aria-label="Odśwież">
                <RefreshIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </div>
        </div>
        <div className={classes.headerActions}>
          <Button
            variant="outlined"
            startIcon={<ViewColumnIcon />}
            onClick={(e) => setColumnsAnchor(e.currentTarget)}
          >
            Kolumny
          </Button>
          <Button variant="outlined" startIcon={<SummarizeIcon />} onClick={() => setReportOpen(true)}>
            Raport
          </Button>
        </div>
      </div>

      <div className={classes.card}>
        <div className={classes.toolbar}>
          <div className={classes.search}>
            <SearchIcon fontSize="small" />
            <InputBase
              inputRef={searchRef}
              className={classes.searchInput}
              placeholder="Szukaj po nr ref, nr listu, nazwie firmy…"
              value={filters.search}
              onChange={(e) => updateFilters({ search: e.target.value })}
              inputProps={{ "aria-label": "Szukaj" }}
            />
            {filters.search ? (
              <IconButton
                size="small"
                onClick={() => updateFilters({ search: "" })}
                aria-label="Wyczyść wyszukiwanie"
              >
                <ClearIcon fontSize="small" />
              </IconButton>
            ) : (
              <kbd className={classes.kbd}>/</kbd>
            )}
          </div>

          <Badge badgeContent={activeCount} color="primary">
            <Button
              variant={filtersOpen || activeCount > 0 ? "contained" : "outlined"}
              startIcon={<FilterListIcon />}
              onClick={() => setFiltersOpen(true)}
              disableElevation
            >
              Filtry
            </Button>
          </Badge>

          {config.presets.map((preset) => {
            const active = filters[preset.filter] === preset.value;
            const patch = { [preset.filter]: active ? "" : preset.value };
            return (
              <Tooltip key={preset.id} title={preset.hint}>
                <Chip
                  className={classes.presetChip}
                  label={preset.label}
                  color={active ? "primary" : "default"}
                  variant={active ? "filled" : "outlined"}
                  onClick={() => updateFilters(patch)}
                  onDelete={active ? () => updateFilters(patch) : undefined}
                />
              </Tooltip>
            );
          })}

          <span className={classes.toolbarGap} />
          {hasFilters && (
            <Button size="small" onClick={clearAllFilters}>
              Wyczyść filtry
            </Button>
          )}
        </div>

        {hasFilters && (
          <div className={classes.activeFilters}>
            {activeChips.map((chip) => (
              <Chip
                key={chip.id}
                size="small"
                label={chip.label}
                onDelete={() => updateFilters(chip.clear)}
                style={{ background: COLORS.primarySoft, color: COLORS.primaryDark }}
              />
            ))}
          </div>
        )}

        {selectedIds.length > 0 && (
          <div className={classes.selectionBar}>
            <strong>Zaznaczono: {selectedIds.length}</strong>
            <Button
              size="small"
              variant="contained"
              startIcon={<AddCommentIcon />}
              onClick={() => setBulkOpen(true)}
              disableElevation
            >
              Dodaj komentarz
            </Button>
            <Button size="small" onClick={() => setSelectedIds([])}>
              Odznacz wszystko
            </Button>
          </div>
        )}

        <div className={classes.tableWrap}>
          {loading && <LinearProgress className={classes.progress} />}
          <Table stickyHeader size="small" style={{ opacity: loading ? 0.6 : 1, transition: "opacity 0.15s" }}>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox" className={`${classes.headCell} ${classes.headSticky}`} style={stickyHead(0)}>
                  <Tooltip arrow title="Zaznacz / odznacz wszystkie na stronie">
                    <Checkbox
                      size="small"
                      color="primary"
                      checked={pageAllSelected}
                      indeterminate={pageSomeSelected}
                      onChange={toggleSelectAllOnPage}
                      inputProps={{ "aria-label": "Zaznacz wszystkie na stronie" }}
                    />
                  </Tooltip>
                </TableCell>
                <TableCell padding="none" className={`${classes.headCell} ${classes.headSticky}`} style={stickyHead(1)} />
                <TableCell
                  className={`${classes.headCell} ${classes.headSticky}`}
                  style={stickyHead(2, { whiteSpace: "nowrap", boxShadow: "inset -1px 0 0 #e0e0e0" })}
                >
                  Nr Ref
                </TableCell>
                {columns.map((column) => (
                  <TableCell
                    key={column.id}
                    className={classes.headCell}
                    style={{ minWidth: column.width, maxWidth: column.width }}
                  >
                    {column.label}
                  </TableCell>
                ))}
                <TableCell className={classes.headCell} style={{ width: 96 }} />
              </TableRow>
            </TableHead>
            <TableBody>
              {pageRows.map((row) => (
                <Row
                  key={row.id}
                  row={row}
                  config={config}
                  columns={columns}
                  isSelected={selectedSet.has(row.id)}
                  onToggleSelect={toggleRowSelected}
                  onOpen={handleOpenDetail}
                  onDelete={setDeleteTarget}
                  canDelete={canDelete}
                />
              ))}
            </TableBody>
          </Table>

          {!loading && filteredData.length === 0 && (
            <div className={classes.empty}>
              <SearchOffIcon style={{ fontSize: 44, color: "#bdbdbd" }} />
              <strong style={{ fontSize: 17, color: "#424242" }}>Brak zgłoszeń do wyświetlenia</strong>
              <span>
                {hasFilters ? "Żadne zgłoszenie nie spełnia wybranych filtrów." : "Brak zgłoszeń na tej liście."}
              </span>
              {hasFilters && (
                <Button variant="outlined" onClick={clearAllFilters}>
                  Wyczyść filtry
                </Button>
              )}
            </div>
          )}
        </div>

        <TablePagination
          component="div"
          count={filteredData.length}
          page={page}
          rowsPerPage={rowsPerPage}
          rowsPerPageOptions={PAGE_SIZE_OPTIONS}
          onPageChange={(_, newPage) => setPage(newPage)}
          onRowsPerPageChange={(e) => {
            prefs.setPageSize(Number(e.target.value));
            setPage(0);
          }}
          labelRowsPerPage="Wierszy na stronę:"
          labelDisplayedRows={({ from, to, count }) => `${from}–${to} z ${count}`}
        />
      </div>

      {config.legend.length > 0 && (
        <div className={classes.legend}>
          {config.legend.map((item) => (
            <span key={item.label} className={classes.legendItem}>
              <span
                className={classes.legendTone}
                style={{ background: item.background, boxShadow: `inset 3px 0 0 ${item.stripe}` }}
              />
              {item.label}
            </span>
          ))}
        </div>
      )}

      <FilterDrawer
        open={filtersOpen}
        onClose={() => setFiltersOpen(false)}
        onClear={clearAllFilters}
        activeCount={activeCount}
        total={filteredData.length}
        totalLabel="Zgłoszeń"
      >
        <FilterCard
          title="Wyszukiwanie"
          count={countFilled(filters.nrRef, filters.date, ...config.filters.filter((def) => def.type === "text").map((def) => filters[def.key]))}
        >
          <TextField
            label="Nr Ref"
            size="small"
            fullWidth
            multiline
            maxRows={4}
            autoComplete="off"
            value={filters.nrRef}
            onChange={(e) => updateFilters({ nrRef: e.target.value })}
            helperText="Wiele numerów po przecinku lub w nowych liniach"
          />
          <TextField
            label="Data utworzenia (dd-mm-rrrr)"
            size="small"
            fullWidth
            autoComplete="off"
            value={filters.date}
            onChange={(e) => updateFilters({ date: e.target.value })}
          />
          {config.filters.filter((def) => def.type === "text").map(renderFilter)}
        </FilterCard>
        <FilterCard title="Wartości" count={filterCount(config.filters.filter((def) => def.type !== "text"))}>
          {config.filters.filter((def) => def.type !== "text").map(renderFilter)}
        </FilterCard>
        <FilterCard title="Zakres dat" count={rangeCount}>
          {config.ranges.map((r) => (
            <React.Fragment key={r.key}>
              <TextField
                label={`${r.label} od`}
                type="date"
                size="small"
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={filters[`${r.key}From`]}
                onChange={(e) => updateFilters({ [`${r.key}From`]: e.target.value })}
              />
              <TextField
                label={`${r.label} do`}
                type="date"
                size="small"
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={filters[`${r.key}To`]}
                onChange={(e) => updateFilters({ [`${r.key}To`]: e.target.value })}
              />
            </React.Fragment>
          ))}
        </FilterCard>
      </FilterDrawer>

      <ColumnsMenu
        anchorEl={columnsAnchor}
        onClose={() => setColumnsAnchor(null)}
        columns={config.columns}
        hiddenColumns={prefs.hiddenColumns}
        onToggle={prefs.toggleColumn}
        onReset={prefs.resetColumns}
      />

      <Dialog open={reportOpen} onClose={closeReport} fullWidth maxWidth="xs">
        <DialogTitle>{config.report.title}</DialogTitle>
        <DialogContent>
          <div style={{ display: "flex", flexDirection: "column", gap: 20, paddingTop: 8 }}>
            <TextField
              label="Data początkowa"
              type="date"
              value={reportStart}
              onChange={(e) => setReportStart(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="Data końcowa"
              type="date"
              value={reportEnd}
              onChange={(e) => setReportEnd(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            {config.report.directions && (
              <Autocomplete
                multiple
                options={sortPl(apiData.map((row) => row.kierunek))}
                value={reportDirections}
                onChange={(_, value) => setReportDirections(value)}
                noOptionsText="Brak opcji"
                renderInput={(params) => <TextField {...params} label="Kierunek (opcjonalnie)" />}
              />
            )}
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeReport}>Anuluj</Button>
          <Button onClick={handleGenerateReport} variant="contained" disableElevation>
            Generuj
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={bulkOpen} onClose={() => !bulkSaving && setBulkOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Dodaj komentarz do {selectedIds.length} zaznaczonych zgłoszeń</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Komentarz"
            fullWidth
            multiline
            minRows={4}
            value={bulkText}
            onChange={(e) => setBulkText(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkOpen(false)} disabled={bulkSaving}>
            Anuluj
          </Button>
          <Button onClick={handleSaveBulkComment} variant="contained" disabled={bulkSaving || !bulkText.trim()}>
            {bulkSaving ? "Zapisywanie..." : "Zapisz komentarz"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(deleteTarget)} onClose={() => setDeleteTarget(null)}>
        <DialogTitle>Usunąć zgłoszenie?</DialogTitle>
        <DialogContent>
          Zgłoszenie <strong>{deleteTarget?.nrRef}</strong> zostanie trwale usunięte.
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>Anuluj</Button>
          <Button onClick={handleConfirmDelete} color="error" variant="contained">
            Usuń
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default MagazynListPage;
